import { useMutation } from "@apollo/react-hooks"
import {
  Button,
  Checkbox,
  CircularProgress,
  createStyles,
  fade,
  FormControl,
  FormControlLabel,
  FormHelperText,
  makeStyles,
  Radio,
  RadioGroup,
  Theme,
} from "@material-ui/core"
import CloseIcon from "@material-ui/icons/Close"
import React, { useEffect, useRef, useState } from "react"
import { isMobile } from "react-device-detect"
import { useGlobalStyles } from "../../styles/globalStyles"
import TextButton from "../elements/TextButton"
import BankCardCheck from "../icons/BankCardCheck"
import { DEFAULT_MESSAGE } from "./constants"
import {
  PaycheckApplicationModal,
  PaycheckConfirmationModal,
  PaycheckIndexModal,
  PaycheckTermAndConditionModal,
} from "./elements/modals"
import { PaycheckInput, PaycheckInputAdornment } from "./elements/sidebar"
import {
  CREATE_ADVANCE_MUTATION,
  GENERATE_ADVANCE_QUOTE_MUTATION,
  REQUEST_ADVANCE_PAYMENT_OPTIONS,
  REQUEST_EMPLOYEE_INDEX_STATUS,
} from "./graphql"
import SideBarTable from "./tables/SideBarTable"
import "../../styles/glowStyles.css"
import { usePointerGlow } from "../../utils/MousePointerGlow"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    transactionSideBar: {
      position: "fixed",
      right: 0,
      top: 0,
      height: "100%",
      width: isMobile ? "100%" : "28%",
      background: theme.palette.secondary.dark,
      zIndex: 1300,
      padding: "35px 25px",
      overflowY: "auto",
    },
    closeButton: {
      borderRadius: "50%",
      background: "#fff",
      width: "initial",
      minWidth: "initial",
      padding: "6px",
      float: "right",
    },
    transactionWrapper: {
      position: "relative",
      borderRadius: "5px", // Border radius for the wrapper
      background: theme.palette.secondary.dark,
      minHeight: "100px",
      marginTop: "30px",
      color: "#fff",
      padding: "25px 15px",
      border: "3px solid hsl(0 0% 100% / 0.15)",
    },
    transactionStep: {},
    transactionStepHeader: {
      paddingLeft: "32px",
      display: "flex",
      flexDirection: "row",
    },
    transactionStepBody: {
      padding: "10px 0 27px 0",
    },
    transactionStepNumber: {
      padding: "10px",
      background: "hsl(0 0% 100% / 0.15)",
      color: "#fff",
      borderRadius: "50%",
      display: "block",
      minWidth: "32px",
      textAlign: "center",
      height: "32px",
      lineHeight: 1,
      marginLeft: "-36px",
    },
    transactionStepText: {
      lineHeight: 1,
      alignSelf: "center",
      display: "block",
      marginLeft: "6px",
      color: '#fff',
      fontSize: isMobile ? "11px" : "13px",
      fontWeight: "bold",
    },
    transactionPaymentOptionText: {
      marginLeft: "2rem",
      fontWeight: "bold",
      color: theme.palette.secondary.main,
    },
    transactionQuote: {
      background: theme.palette.secondary.main,
      width: "100%",
      minHeight: "40px",
      borderRadius: "5px",
      color: theme.palette.secondary.dark,
      padding: "15px",
      overflowX: "auto",
    },
    quoteDatesSmall: {
      color: "#A5C2D6",
    },
    quoteDateSpan: {
      color: "#ffffff",
    },
    linkItem: {
      color: theme.palette.primary.main,
      cursor: "pointer",
    },
    radioRoot: {
      color: theme.palette.secondary.main, // Inactive radio color
      "&.Mui-checked": {
        color: theme.palette.primary.main, // Active radio color
      },
    },
    checkboxRoot: {
      color: theme.palette.secondary.main, // Inactive color
      "&.Mui-checked": {
        color: theme.palette.secondary.main, // Active color when checked
      },
    },
    applyForWithdrawal: {
      borderRadius: "8px",
      // width: "100%",
      cursor: "pointer",
      color: "#fff",
      // padding: "0 15px!important",
      textAlign: "left",
      // margin: isMobile ? "4px" : "20px -15px 0px -15px!important",
      lineHeight: "1.2",
      background: `linear-gradient(135deg, #D6292E 0%, #AE242F 50%, #F2E928 100%)`, // Gradient from red to yellow
      border: "2px solid", // Border with the gradient colors
      borderImage: `linear-gradient(135deg, #D6292E, #AE242F, #F2E928) 1`,
      boxShadow: `
        -5px -5px 10px #D6292E,  /* Red glow restricted to the top-left */
        0px 0px 15px #AE242F,     /* Dark red glow in the middle */
        5px 5px 25px #F2E928      /* Yellow glow on the bottom-right only */
      `,
      backgroundColor: theme.palette.primary.dark, // Fallback background
      transition: "box-shadow 0.3s ease-in-out",
      "&:hover": {
        boxShadow: `
          -7px -7px 15px #D6292E,  /* Stronger red glow at top-left on hover */
          0px 0px 20px #AE242F,     /* Stronger dark red glow in the middle */
          7px 7px 30px #F2E928      /* Stronger yellow glow at bottom-right on hover */
        `,
      },
    },
  }),
)

const TransactionSideBar: React.FC<any> = (props: any) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const companyName =
    (process.env.COMPANY_NAME || "paycheck").charAt(0).toUpperCase() +
    (process.env.COMPANY_NAME || "paycheck").slice(1)
  const globalStyles = useGlobalStyles()
  const classes = useStyles()
  const {
    onClose,
    open,
    accessibleAmount,
    refetch,
    employeeId,
    celbuxNumber,
  } = props
  const [submitAdvanceAmount, generateAdvanceQuoteRes] = useMutation(
    GENERATE_ADVANCE_QUOTE_MUTATION,
  )
  const [requestPaymentOptions, requestPaymentOptionRes] = useMutation(
    REQUEST_ADVANCE_PAYMENT_OPTIONS,
  )
  const [
    requestEmployeeIndexStatus,
    requestEmployeeIndexStatusRes,
  ] = useMutation(REQUEST_EMPLOYEE_INDEX_STATUS)

  const [submitCreateAdvance, createAdvanceRes] = useMutation(
    CREATE_ADVANCE_MUTATION,
  )
  const [submittingCreateAdvance, setSubmittingCreateAdvance] = React.useState(
    false,
  )
  const [advanceAmountError, setAdvanceAmountError] = React.useState("")

  const [acceptedTsAndCs, setAcceptedTsAndCs] = React.useState(false)
  const [tAndCTimestamp, setTAndCTimestamp] = React.useState("")
  const [displayTcAndCsInModal, setDisplayTcAndCsInModal] = React.useState(
    false,
  )
  const [
    displayPaycheckTransaction,
    setDisplayPaycheckTransaction,
  ] = React.useState(false)
  const [displayConfirmation, setDisplayConfirmation] = useState(false)
  const [
    displayPaycheckIndexModal,
    setDisplayPaycheckIndexModal,
  ] = React.useState(false)

  const [enteredAmount, setEnteredAmount] = React.useState("")
  const setInitialQuoteData = () => {
    return {
      quote: generateAdvanceQuoteRes.data?.generateAdvanceQuote?.quote,
      quoteLoading: generateAdvanceQuoteRes.loading,
      quoteMessage: generateAdvanceQuoteRes.error
        ? generateAdvanceQuoteRes.error.message
        : DEFAULT_MESSAGE,
    }
  }

  const [advanceData, setAdvanceData] = useState({
    advance: createAdvanceRes.data?.createAdvance?.advance,
    createAdvanceError: createAdvanceRes.error?.message,
  })
  const [quoteData, setQuoteData] = useState(setInitialQuoteData())
  const [paymentOptions, setPaymentOptions] = useState([])
  const [selectedPaymentOption, setSelectedPaymentOption] = React.useState("")

  const isCelbux = selectedPaymentOption === "celbux"

  const isAdvanceAmountValid = (amount: number) => {
    if (isNaN(amount)) {
      return { valid: false, message: "Amount must be a valid number" }
    } else if (amount > accessibleAmount) {
      return {
        valid: false,
        message: `Amount cannot exceed the accessible amount of R${accessibleAmount}`,
      }
    } else if (amount <= 0) {
      return { valid: false, message: "Amount must be greater than 0" }
    }
    return { valid: true, message: "" }
  }

  const handleAdvanceAmountChange = () => {
    const advanceAmount = parseFloat(enteredAmount)
    const { valid, message } = isAdvanceAmountValid(advanceAmount)
    setAdvanceAmountError(message)
    if (valid && selectedPaymentOption) {
      submitAdvanceAmount({
        variables: {
          advanceAmount,
          paymentOption: selectedPaymentOption,
        },
      })
        .then((data: any) => {
          setQuoteData({
            ...quoteData,
            quote: data.data?.generateAdvanceQuote?.quote,
            quoteLoading: data.data?.loading,
            quoteMessage: data.data?.error
              ? data?.error.message
              : DEFAULT_MESSAGE,
          })
        })
        .catch((error) => {
          setQuoteData({
            ...quoteData,
            quote: null,
            quoteMessage: error.message,
          })
        })
    }
  }
  let executionTimer: NodeJS.Timeout | undefined

  usePointerGlow(sidebarRef);

  useEffect(() => {
    if (employeeId && enteredAmount.length > 2) {
      if (executionTimer) {
        clearTimeout(executionTimer)
      }
      executionTimer = setTimeout(() => {
        requestPaymentOptions({
          variables: {
            employee: employeeId,
            platform: "web",
            requestAmount: +enteredAmount ?? 0,
          },
        })
      }, 500)
    }
  }, [employeeId, enteredAmount])

  useEffect(() => {
    setPaymentOptions(
      requestPaymentOptionRes?.data?.requestPaymentOption?.paymentOptions
        ? JSON.parse(
            requestPaymentOptionRes?.data?.requestPaymentOption?.paymentOptions,
          )
        : [],
    )
  }, [requestPaymentOptionRes?.data])

  useEffect(() => {
    if (enteredAmount && selectedPaymentOption) {
      handleAdvanceAmountChange()
    }
  }, [enteredAmount, selectedPaymentOption])

  useEffect(() => {
    setSelectedPaymentOption("")
  }, [paymentOptions])

  const handlePaymentOptionRadioChange = (event: {
    target: { value: React.SetStateAction<string> }
  }) => {
    setSelectedPaymentOption(event.target.value)
  }

  const handleTransactionTcAndCsCheck = () => {
    setTAndCTimestamp(new Date().toLocaleString())
    setAcceptedTsAndCs(!acceptedTsAndCs)
  }

  const handlePaycheckTransactionApplication = () => {
    const { advanceAmount, ...quote } = quoteData.quote
    const termAndConditionAcceptTimestamp = tAndCTimestamp
    setSubmittingCreateAdvance(true)
    submitCreateAdvance({
      variables: {
        advanceAmount: advanceAmount,
        termAndConditionAcceptTimestamp: termAndConditionAcceptTimestamp,
        paymentOption: selectedPaymentOption,
      },
    })
      .then((data: any) => {
        setAdvanceData({
          ...advanceData,
          advance: data.data?.createAdvance?.advance,
        })
        setQuoteData({
          ...quoteData,
          quote: data.data?.generateAdvanceQuote?.quote,
          quoteLoading: data.data?.loading,
          quoteMessage: data.data?.error
            ? data?.error.message
            : DEFAULT_MESSAGE,
        })
      })
      .catch((error) => {
        setAdvanceData({
          ...advanceData,
          createAdvanceError: error.message,
        })
      })
      .finally(() => {
        setDisplayConfirmation(false)
        requestEmployeeIndexStatus({
          variables: {
            employee: employeeId,
          },
        }).then((data) => {
          const showIndexModal =
            data?.data?.getEmployeeIndexAlertStatus?.hasFinancialDistressAlert
          if (showIndexModal) {
            setDisplayPaycheckIndexModal(showIndexModal)
            setDisplayPaycheckTransaction(!showIndexModal)
          } else {
            setDisplayPaycheckTransaction(true)
          }
        })
      })
  }

  const handleDoneClick = () => {
    refetch()
    onClose()
    setSubmittingCreateAdvance(false)
    setDisplayPaycheckTransaction(false)
    setAcceptedTsAndCs(false)
    setEnteredAmount("")
    setTAndCTimestamp("")
    setSelectedPaymentOption("")
    setQuoteData({ ...setInitialQuoteData(), quote: null })
  }

  const handleApplyClick = () => {
    setDisplayConfirmation(true)
  }

  const handleModalClose = () => {
    if (submittingCreateAdvance) {
      handleDoneClick()
    } else {
      setDisplayPaycheckTransaction(false)
    }
  }

  const canClickApply = () => {
    if (quoteData.quote && acceptedTsAndCs && selectedPaymentOption) {
      return true
    }
    return false
  }

  const handleEnteredAmountChange = (
    event: React.FocusEvent<HTMLInputElement>,
  ) => {
    setEnteredAmount(event.target.value)
  }

  const showTermsAndConditions = () => {
    setDisplayTcAndCsInModal(true)
  }

  return (
    <>
      <div
        style={{ display: open ? "block" : "none" }}
        className={classes.transactionSideBar}
      >
        <div style={{ clear: "both", height: "38px" }}>
          <Button className={classes.closeButton} onClick={onClose}>
            <CloseIcon />
          </Button>
        </div>
        <div ref={sidebarRef} className={classes.transactionWrapper} data-glow>
          <h4 className={globalStyles.paperHead}>
            <BankCardCheck /> Request{" "}
            <span style={{ textTransform: "capitalize" }}>{companyName}</span>{" "}
            transaction
          </h4>
          <div>
            <div className={classes.transactionStep}>
              <div className={classes.transactionStepHeader}>
                <span className={classes.transactionStepNumber}>1</span>{" "}
                <span className={classes.transactionStepText}>
                  Specify the amount you would like as a{" "}
                  <span style={{ textTransform: "capitalize" }}>
                    {companyName}
                  </span>{" "}
                  for this transaction.
                </span>
              </div>
              <div className={classes.transactionStepBody}>
                <FormControl error={advanceAmountError != ""}>
                  <PaycheckInput
                    onChange={handleEnteredAmountChange}
                    startAdornment={
                      <PaycheckInputAdornment position="start">
                        R
                      </PaycheckInputAdornment>
                    }
                    id="advanceAmount"
                    value={enteredAmount}
                  />
                  {advanceAmountError != "" ? (
                    <FormHelperText
                      style={{
                        background: "#fff",
                        borderRadius: "5px",
                        padding: "4px",
                      }}
                    >
                      {advanceAmountError}
                    </FormHelperText>
                  ) : (
                    ""
                  )}
                </FormControl>
              </div>
            </div>
            <div className={classes.transactionStep}>
              <div className={classes.transactionStepHeader}>
                <span className={classes.transactionStepNumber}>2</span>{" "}
                <span className={classes.transactionStepText}>
                  Select a Payment Option
                </span>
              </div>

              <div className={classes.transactionStepBody}>
                <FormControl error={advanceAmountError != ""}>
                  <RadioGroup
                    aria-label="payment_options"
                    color=""
                    name="selectPaymentOption"
                    value={selectedPaymentOption}
                    onChange={handlePaymentOptionRadioChange}
                  >
                    {paymentOptions.map((item) => (
                      <FormControlLabel
                        style={{ fontSize: "14px !important" }}
                        key={item[0]}
                        value={item[0]}
                        // control={<Radio />}
                        control={<Radio classes={{ root: classes.radioRoot }} />}
                        label={
                          <span className={classes.transactionStepText}>
                            {item[1]}
                          </span>
                        }
                      />
                    ))}
                    {!requestPaymentOptionRes.loading &&
                      !paymentOptions.length && (
                        <span className={classes.transactionPaymentOptionText}>
                          You do not have any payment options on your account.
                        </span>
                      )}
                    {requestPaymentOptionRes.loading &&
                      !paymentOptions.length && (
                        <span className={classes.transactionPaymentOptionText}>
                          Fetching payment option, please wait...
                        </span>
                      )}
                  </RadioGroup>
                  {isCelbux && (
                    <FormHelperText
                      style={{
                        color: "lawngreen",
                        fontSize: "14px",
                        borderRadius: "5px",
                        padding: "4px",
                      }}
                    >
                      Transaction will made into the celbux number:{" "}
                      <b>{celbuxNumber}</b>
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
            </div>
            <div className={classes.transactionStep}>
              <div className={classes.transactionStepHeader}>
                <span className={classes.transactionStepNumber}>3</span>{" "}
                <span className={classes.transactionStepText}>
                  Projected transaction details.
                </span>
              </div>
              <div className={classes.transactionStepBody}>
                <div className={classes.transactionQuote}>
                  {quoteData.quoteLoading || generateAdvanceQuoteRes.loading ? (
                    <CircularProgress color="primary"/>
                  ) : quoteData.quote ? (
                    <SideBarTable quoteData={quoteData} />
                  ) : (
                    quoteData.quoteMessage
                  )}
                </div>
              </div>
            </div>
            <div className={classes.transactionStep}>
              <div className={classes.transactionStepHeader}>
                <span className={classes.transactionStepNumber}>4</span>{" "}
                <span className={classes.transactionStepText}>
                  I have read the{" "}
                  <a
                    onClick={showTermsAndConditions}
                    className={classes.linkItem}
                  >
                    terms and conditions
                  </a>{" "}
                  <Checkbox
                    classes={{ root: classes.checkboxRoot }}
                    color="secondary"
                    checked={acceptedTsAndCs}
                    onChange={handleTransactionTcAndCsCheck}
                    name="personal.nameAndSurname"
                  />
                </span>
              </div>
              <div className={classes.transactionStepBody}></div>
            </div>
            <TextButton
              disabled={!canClickApply()}
              // className={classes.applyForWithdrawal}
              onClick={handleApplyClick}
              style={{ width: "100%", color: "#ffffff" , background: "linear-gradient(135deg, #D6292E 0%, #AE242F 50%, #F2E928 100%)"}}
              text={`Apply for ${companyName} Withdrawal`}
            />
          </div>
        </div>
      </div>
      {/* MODAL SECTION */}
      <PaycheckTermAndConditionModal
        displayTcAndCsInModal={displayTcAndCsInModal}
        handleOnClose={() => setDisplayTcAndCsInModal(false)}
      />
      <PaycheckConfirmationModal
        displayConfirmation={displayConfirmation}
        advanceAmount={quoteData.quote?.advanceAmount}
        isCelbux={isCelbux}
        celbuxNumber={celbuxNumber}
        handleOnClose={() => setDisplayConfirmation(false)}
        handleOnConfirm={handlePaycheckTransactionApplication}
        isLoading={createAdvanceRes.loading}
      />
      <PaycheckApplicationModal
        displayPaycheckTransaction={displayPaycheckTransaction}
        isLoading={createAdvanceRes.loading}
        hasError={!(advanceData.advance && !advanceData.createAdvanceError)}
        error={advanceData.createAdvanceError}
        handleOnClose={handleModalClose}
        handleOnDone={handleDoneClick}
      />
      <PaycheckIndexModal
        displayPaycheckIndexModel={displayPaycheckIndexModal}
        handleOnClose={() => {
          setDisplayPaycheckIndexModal(false)
          setDisplayPaycheckTransaction(true)
        }}
        employeeId={employeeId}
      />
    </>
  )
}

export default TransactionSideBar
